import { useState, useCallback, useEffect, useRef, Fragment } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '@ui-kit';
import { ThemeToggle } from '../../_shared/ThemeToggle';
import { LanguageToggle } from '../../_shared/LanguageToggle';
import { CommonUsagePanel } from '../../_shared/CommonUsagePanel';
import { APP_VERSION } from '../../_shared/appVersion';
import { useLogin } from '../hooks/useLogin';
import { loginSchema, type LoginFormValues } from '../schema/loginSchema';
import { getStoredToken } from '../api/loginApi';
import type { CommonUsageItem } from '../../_shared/CommonUsagePanel';

/** §6.7.1 공통 모듈 16행 */
const COMMON_USAGE_ITEMS: CommonUsageItem[] = [
  {
    module: 'eraf-core',
    moduleColor: 'blue',
    api: 'ApiResponse<LoginResponse> (C002)',
    role: '로그인 응답 표준 wrapper',
    snippet: 'return ApiResponse.success(loginService.login(req));',
  },
  {
    module: 'eraf-core',
    moduleColor: 'blue',
    api: 'ErrorResponse (C002)',
    role: '4xx/5xx 오류 본문 (FE 매핑)',
    snippet: 'if (!res.success) showError(res.error.code);',
  },
  {
    module: 'eraf-core',
    moduleColor: 'blue',
    api: 'BusinessException(AUTH-401-001) (C001)',
    role: '자격증명 실패 시 throw',
    snippet: 'throw new BusinessException(ErrorCode.AUTH_INVALID_CREDENTIAL);',
  },
  {
    module: 'eraf-core',
    moduleColor: 'blue',
    api: 'GlobalExceptionHandler (C001)',
    role: '컨트롤러 advice (재사용, biz 코드 없음)',
    snippet: '// 자동 적용 — biz 코드 없음',
  },
  {
    module: 'eraf-core',
    moduleColor: 'blue',
    api: '@Email, @Password, @NoXss (C003)',
    role: 'LoginRequest 검증',
    snippet: '@Email @NotBlank private String email;',
  },
  {
    module: 'eraf-core',
    moduleColor: 'blue',
    api: 'TraceIdFilter (C007)',
    role: 'traceId · userId MDC 주입',
    snippet: 'log.info("login attempt user={}", maskedEmail);',
  },
  {
    module: 'eraf-core',
    moduleColor: 'blue',
    api: 'StringUtils.mask(...) (C004)',
    role: '로그 내 이메일 마스킹',
    snippet: 'StringUtils.mask(email) // → u***@skax.com',
  },
  {
    module: 'eraf-data-jpa',
    moduleColor: 'green',
    api: 'AuditableEntity (C039)',
    role: 'UserAccount 감사 6종 자동 매핑',
    snippet: 'class UserAccount extends AuditableEntity {}',
  },
  {
    module: 'eraf-data-jpa',
    moduleColor: 'green',
    api: 'GenericRepository<T,ID> (C042)',
    role: 'UserAccountRepository 부모',
    snippet:
      'interface UserAccountRepository extends GenericRepository<UserAccount, Long>',
  },
  {
    module: 'eraf-security',
    moduleColor: 'yellow',
    api: 'JwtAuthenticationService.issue(LoginCommand) (C028)',
    role: '인증 + 토큰 발급 위임 (24h vs 30d 분기)',
    snippet: 'return jwtAuthenticationService.issue(cmd);',
  },
  {
    module: 'eraf-security',
    moduleColor: 'yellow',
    api: 'JwtAuthenticationService.refresh/revoke (C028)',
    role: 'refresh / logout / me 처리',
    snippet: 'jwtAuthenticationService.revoke(jti);',
  },
  {
    module: 'eraf-security',
    moduleColor: 'yellow',
    api: 'LoginAttemptService (C028)',
    role: '5회 실패 추적 + 영구 잠금 트리거',
    snippet: '// JwtAuthenticationService.issue 내부 자동 호출',
  },
  {
    module: 'eraf-security',
    moduleColor: 'yellow',
    api: 'JwtBlacklist (Redis + JPA dual store) (C028)',
    role: 'logout · refresh 회전 시 jti 등록',
    snippet: 'jwtBlacklist.add(jti, expiresAt); // 자동',
  },
  {
    module: 'eraf-security',
    moduleColor: 'yellow',
    api: 'JwtAuthenticationFilter (C028)',
    role: '보호 경로 처리 (SecurityFilterChain)',
    snippet: '// SecurityFilterChain 자동 적용',
  },
  {
    module: 'eraf-security',
    moduleColor: 'yellow',
    api: 'SecurityChainCustomizer · PathPolicy (C029)',
    role: '4개 경로 인증 정책',
    snippet: 'PathPolicy.permitAll("/api/eraf/auth/login")',
  },
  {
    module: 'eraf-security',
    moduleColor: 'yellow',
    api: 'BCryptPasswordEncoder (cost=12)',
    role: 'passwordHash 검증',
    snippet: 'passwordEncoder.matches(raw, hash)',
  },
];

/**
 * §6.9.7 — headline 의 `<br>` 마커를 raw HTML 삽입 없이 렌더 (XSS 회피).
 * i18n 값은 리터럴 문자열 "<br>" 을 split 지점으로만 사용한다.
 */
function renderWithLineBreaks(text: string) {
  const parts = text.split('<br>');
  return parts.map((part, idx) => (
    <Fragment key={idx}>
      {part}
      {idx < parts.length - 1 && <br />}
    </Fragment>
  ));
}

// ─── Toast ──────────────────────────────────────────────────────────────────

function Toast({ message, onClose }: { message: string; onClose: () => void }) {
  useEffect(() => {
    const id = setTimeout(onClose, 3000);
    return () => clearTimeout(id);
  }, [onClose]);

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        bottom: 'var(--spacing-6)',
        left: '50%',
        transform: 'translateX(-50%)',
        background: 'var(--color-surface-elevated)',
        border: '1px solid var(--color-surface-border)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--spacing-3) var(--spacing-4)',
        fontSize: 'var(--font-size-sm)',
        color: 'var(--color-text-primary)',
        zIndex: 2000,
        boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
        maxWidth: '360px',
        textAlign: 'center',
      }}
    >
      {message}
    </div>
  );
}

// ─── LockWarnDialog ──────────────────────────────────────────────────────────

function LockWarnDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const confirmRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    confirmRef.current?.focus();
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    },
    [onClose]
  );

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="dialog-title-lockwarn"
      aria-describedby="dialog-desc-lockwarn"
      onKeyDown={handleKeyDown}
    >
      <div
        style={{
          background: 'var(--color-surface-muted)',
          border: '1px solid var(--color-surface-border)',
          borderRadius: 'var(--radius-lg)',
          padding: 'var(--spacing-6)',
          width: '360px',
          boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          id="dialog-title-lockwarn"
          style={{
            fontSize: 'var(--font-size-lg)',
            fontWeight: 700,
            color: 'var(--color-warning)',
            marginBottom: 'var(--spacing-4)',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--spacing-2)',
          }}
        >
          <span aria-hidden="true">⚠️</span> {t('login.dialog.lockWarn.title')}
        </div>
        <div
          id="dialog-desc-lockwarn"
          style={{
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-text-secondary)',
            lineHeight: 1.7,
            marginBottom: 'var(--spacing-6)',
          }}
        >
          <p>
            {t('login.dialog.lockWarn.line1')}{' '}
            <strong style={{ color: 'var(--color-danger)' }}>{t('login.dialog.lockWarn.permanent')}</strong>
            {t('login.dialog.lockWarn.line2')}
          </p>
          <p style={{ marginTop: 'var(--spacing-3)' }}>
            {t('login.dialog.lockWarn.line3')}
          </p>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            ref={confirmRef}
            type="button"
            aria-label={t('login.dialog.lockWarn.confirm.aria')}
            onClick={onClose}
            style={{
              padding: 'var(--spacing-2) var(--spacing-6)',
              background: 'var(--color-brand-primary)',
              color: 'var(--color-text-inverse)',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'var(--font-family-sans)',
            }}
          >
            {t('login.dialog.lockWarn.confirm')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── RememberMeTooltip ───────────────────────────────────────────────────────

function RememberMeTooltip({ message, testId }: { message: string; testId: string }) {
  const [visible, setVisible] = useState(false);

  return (
    <span
      style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
      onFocus={() => setVisible(true)}
      onBlur={() => setVisible(false)}
    >
      <span
        tabIndex={0}
        role="img"
        aria-label={message}
        style={{
          width: '16px',
          height: '16px',
          borderRadius: 'var(--radius-full)',
          border: '1px solid var(--color-text-muted)',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 'var(--font-size-xs)',
          color: 'var(--color-text-muted)',
          cursor: 'default',
          userSelect: 'none',
          flexShrink: 0,
          lineHeight: 1,
        }}
      >
        ?
      </span>
      {visible && (
        <span
          role="tooltip"
          data-testid={testId}
          style={{
            position: 'absolute',
            bottom: 'calc(100% + var(--spacing-1))',
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'var(--color-surface-elevated)',
            border: '1px solid var(--color-surface-border)',
            borderRadius: 'var(--radius-md)',
            padding: 'var(--spacing-2) var(--spacing-3)',
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-text-secondary)',
            whiteSpace: 'normal' as const,
            zIndex: 100,
            boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
            maxWidth: '280px',
            lineHeight: 1.5,
          }}
        >
          {message}
        </span>
      )}
    </span>
  );
}

// ─── Feature flags ────────────────────────────────────────────────────────────

const features = {
  passwordResetEnabled: false,
};

// ─── LoginPage ───────────────────────────────────────────────────────────────

export function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const loginMutation = useLogin();
  const { t } = useTranslation();

  // #476: detect redirect-due-to-expiry via ?expired=1
  const isSessionExpired = searchParams.get('expired') === '1';

  const [toast, setToast] = useState<string | null>(null);
  const [showDialog, setShowDialog] = useState(false);
  // Track failure count for 4-fail dialog
  const failCountRef = useRef(0);

  // R7-031: use ref for mutate to avoid stale closure in onSubmit
  const mutateRef = useRef(loginMutation.mutate);
  useEffect(() => {
    mutateRef.current = loginMutation.mutate;
  }, [loginMutation.mutate]);

  // If already authenticated (and NOT an expiry redirect), redirect to _index
  useEffect(() => {
    if (getStoredToken() && !isSessionExpired) {
      navigate('/_index', { replace: true });
    }
  }, [navigate, isSessionExpired]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '', rememberMe: false },
    // R6-003: onTouched shows inline errors after blur, not while typing
    mode: 'onTouched',
  });

  const rememberMeChecked = useWatch({ control, name: 'rememberMe' });

  const isPending = loginMutation.isPending;
  const errorCode = loginMutation.error
    ? (loginMutation.error as { code?: string })?.code ?? ''
    : '';
  const isLocked = errorCode === 'AUTH-423-001';
  const isInactive = errorCode === 'AUTH-403-002';
  const isError = loginMutation.isError;

  // R7-018: friendly HTTP-400 messages — map known error codes to i18n keys
  const getErrorMessage = () => {
    if (!isError) return '';
    if (isLocked) return t('login.banner.locked.message');
    if (isInactive) return t('login.banner.error.inactive');
    if (errorCode === 'AUTH-401-001') return t('login.banner.error.invalidCredentials');
    if (
      errorCode === 'VALIDATION-400-001' ||
      errorCode === 'COMMON-400-001' ||
      errorCode.startsWith('HTTP-400')
    ) {
      return t('login.banner.error.validation');
    }
    if (errorCode.startsWith('HTTP-0') || errorCode === 'Network Error') {
      return t('login.banner.error.network');
    }
    const raw = (loginMutation.error as { message?: string })?.message ?? '';
    if (raw && !raw.startsWith('Request failed') && !raw.startsWith('HTTP ')) {
      return raw;
    }
    return t('login.banner.error.unknown');
  };

  // R7-031: R6-005 fix retained — use `err` from onError callback (not stale loginMutation.error)
  // R6-004: show toast on AUTH-423-001 (locked account)
  const onSubmit = useCallback(
    (values: LoginFormValues) => {
      // R7-031: call via ref so this callback never captures a stale mutate
      mutateRef.current(values, {
        onError: (err) => {
          const code = (err as { code?: string })?.code ?? '';
          if (code === 'AUTH-423-001') {
            setToast(t('login.toast.accountLocked'));
          } else {
            failCountRef.current += 1;
            // Show lock-warn dialog on the 4th failure
            if (failCountRef.current === 4) {
              setShowDialog(true);
            }
          }
        },
      });
    },
    // t is stable; mutateRef is a ref so no re-create needed
    [t]
  );

  const handleForgotPassword = useCallback(() => {
    navigate('/password-reset/request');
  }, [navigate]);

  const handleRetry = useCallback(() => {
    loginMutation.reset();
    reset();
  }, [loginMutation, reset]);

  // Determine status pill state
  const statusPillLabel = isPending
    ? t('login.status.pending')
    : isLocked
    ? t('login.status.locked')
    : isError
    ? t('login.status.error')
    : t('login.status.ok');

  const statusPillColor: React.CSSProperties =
    isPending
      ? { color: 'var(--color-text-muted)', borderColor: 'var(--color-text-muted)' }
      : isError
      ? { color: 'var(--color-danger)', borderColor: 'var(--color-danger)' }
      : { color: 'var(--color-success)', borderColor: 'var(--color-success)' };

  return (
    <div className="lr-page" aria-busy={isPending}>
      {/* Lock-warn dialog (§6.4 dialog state) */}
      {showDialog && <LockWarnDialog onClose={() => setShowDialog(false)} />}

      {/* Toast notification */}
      {toast && <Toast message={toast} onClose={() => setToast(null)} />}

      {/* ── Brand panel (좌) — §6.9.1 ──────────────────────────────────── */}
      <section className="lr-brand" aria-label={t('login.brand.badge')}>
        <div className="lr-blob lr-blob-1" aria-hidden="true" />
        <div className="lr-blob lr-blob-2" aria-hidden="true" />
        <div className="lr-blob lr-blob-3" aria-hidden="true" />
        <div className="lr-veil" aria-hidden="true" />
        <div className="lr-brand-inner">
          <div className="lr-logo">
            <div className="lr-logo-mark" aria-hidden="true">e</div>
            <span className="lr-logo-text">eraf</span>
          </div>
          <div className="lr-brand-body">
            <div className="lr-badge">{t('login.brand.badge')}</div>
            <h2 className="lr-headline">{renderWithLineBreaks(t('login.brand.headline'))}</h2>
            <p className="lr-desc">{t('login.brand.description')}</p>
            <ul className="lr-features">
              <li>
                <span className="lr-dot">◆</span>
                <span>{t('login.brand.feature1')}</span>
              </li>
              <li>
                <span className="lr-dot">◆</span>
                <span>{t('login.brand.feature2')}</span>
              </li>
              <li>
                <span className="lr-dot">◆</span>
                <span>{t('login.brand.feature3')}</span>
              </li>
            </ul>
          </div>
          <div className="lr-copyright">{t('login.brand.copyright')}</div>
        </div>
      </section>

      {/* ── Form panel (우) — §6.9.1 ───────────────────────────────────── */}
      <section className="lr-form-panel">
        {/* Topbar — 언어 + 테마 토글 (P10: topbar 로 이전) */}
        <div className="lr-topbar">
          <LanguageToggle size="sm" />
          <ThemeToggle />
        </div>

        <div className="lr-form-wrap">
          <div role="form" aria-label={t('login.form.aria')}>
            {/* Title */}
            <h1 className="lr-form-title">{t('login.form.welcomeTitle')}</h1>
            <p className="lr-form-subtitle">{t('login.form.welcomeSubtitle')}</p>

            {/* Session-expired banner (#476 · P2) — shown when redirected from AuthGuard/interceptor */}
            {isSessionExpired && !isError && (
              <div
                role="alert"
                aria-live="polite"
                aria-atomic="true"
                id="login-session-expired-banner"
                className="lr-banner lr-banner-warning"
              >
                <span aria-hidden="true" className="lr-banner-icon">
                  ⏱
                </span>
                <div style={{ flex: 1 }}>
                  <div className="lr-banner-title" style={{ color: 'var(--color-warning)' }}>
                    {t('common.session.expired.title')}
                  </div>
                  <div className="lr-banner-body">{t('common.session.expired')}</div>
                </div>
              </div>
            )}

            {/* Error banner (§6.4 error state · P1) */}
            {isError && (
              <div
                role="alert"
                aria-live="assertive"
                aria-atomic="true"
                id="login-error-banner"
                className={`lr-banner ${isLocked ? 'lr-banner-warning' : 'lr-banner-danger'}`}
              >
                <span aria-hidden="true" className="lr-banner-icon">
                  {isLocked ? '🔒' : '⚠'}
                </span>
                <div style={{ flex: 1 }}>
                  <div
                    className="lr-banner-title"
                    style={{ color: isLocked ? 'var(--color-warning)' : 'var(--color-danger)' }}
                  >
                    {isLocked ? t('login.banner.locked.title') : t('login.banner.error.title')}
                  </div>
                  <div className="lr-banner-body" style={{ marginBottom: 'var(--spacing-2)' }}>
                    {getErrorMessage()}
                  </div>
                  {/* R7-018: error code chip — show only for auth/server errors, not raw HTTP codes */}
                  {errorCode && !errorCode.startsWith('HTTP-400') && (
                    <code className="lr-error-chip">{errorCode}</code>
                  )}
                  {!isLocked && (
                    <div>
                      <button
                        type="button"
                        aria-label={t('login.banner.retry.aria')}
                        onClick={handleRetry}
                        className="lr-retry-btn"
                      >
                        {t('login.banner.retry')}
                      </button>
                    </div>
                  )}
                  {isLocked && (
                    <div style={{ marginTop: 'var(--spacing-2)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                      {t('login.banner.locked.note')}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Form */}
            <form
              onSubmit={handleSubmit(onSubmit)}
              noValidate
              aria-describedby={isError ? 'login-error-banner' : undefined}
            >
              {/* Email */}
              <div style={{ marginBottom: 'var(--spacing-4)' }}>
                <label htmlFor="login-email" className="lr-field-label">
                  {t('login.form.email.label')}
                </label>
                <input
                  id="login-email"
                  type="email"
                  autoComplete="email"
                  placeholder={t('login.form.email.placeholder')}
                  disabled={isPending || isLocked}
                  aria-invalid={!!errors.email || (isError && !isLocked)}
                  aria-describedby={
                    errors.email ? 'login-email-error' : isError ? 'login-error-banner' : undefined
                  }
                  {...register('email')}
                  className="lr-input"
                  data-invalid={!!errors.email || (isError && !isLocked) || undefined}
                />
                {errors.email && (
                  <span id="login-email-error" role="alert" className="lr-field-error">
                    {errors.email.message === 'login.error.invalidEmail'
                      ? t('login.form.email.error.invalid')
                      : errors.email.message === 'login.error.emailRequired'
                      ? t('login.form.email.error.required')
                      : errors.email.message}
                  </span>
                )}
              </div>

              {/* Password */}
              <div style={{ marginBottom: 'var(--spacing-4)' }}>
                <div className="lr-field-row">
                  <label htmlFor="login-password" className="lr-field-label" style={{ marginBottom: 0 }}>
                    {t('login.form.password.label')}
                  </label>
                  {/* Forgot password link — R10-U-001: FE 진입점 제거 (BE 라우트 보존 · P12) */}
                  {features.passwordResetEnabled && (
                    <button
                      type="button"
                      role="button"
                      aria-label={t('login.forgotPassword.aria')}
                      onClick={handleForgotPassword}
                      className="lr-forgot-link"
                    >
                      {t('login.forgotPassword.label')}
                    </button>
                  )}
                </div>
                <input
                  id="login-password"
                  type="password"
                  autoComplete="current-password"
                  placeholder={t('login.form.password.placeholder')}
                  disabled={isPending || isLocked}
                  aria-invalid={!!errors.password || (isError && !isLocked)}
                  aria-describedby={
                    errors.password ? 'login-password-error' : isError ? 'login-error-banner' : undefined
                  }
                  {...register('password')}
                  className="lr-input"
                  data-invalid={!!errors.password || (isError && !isLocked) || undefined}
                />
                {errors.password && (
                  <span id="login-password-error" role="alert" className="lr-field-error">
                    {errors.password.message === 'login.error.passwordRequired'
                      ? t('login.form.password.error.required')
                      : errors.password.message}
                  </span>
                )}
              </div>

              {/* Remember Me (P5/P11) — custom-styled checkbox, native input kept fully interactive */}
              <div className="lr-remember-row">
                <label
                  className="lr-remember-label"
                  aria-label={t('login.form.rememberMe.aria')}
                  style={{ cursor: isPending || isLocked ? 'not-allowed' : 'pointer', opacity: isPending || isLocked ? 0.5 : 1 }}
                >
                  <input
                    type="checkbox"
                    disabled={isPending || isLocked}
                    aria-label={t('login.form.rememberMe')}
                    {...register('rememberMe')}
                    className="lr-checkbox-input"
                  />
                  <span>{t('login.form.rememberMe')}</span>
                </label>
                {/* R10-U-001: 양방향 툴팁 — checked/unchecked 메시지 분리 */}
                <RememberMeTooltip
                  message={rememberMeChecked ? t('login.rememberMe.tooltip.checked') : t('login.rememberMe.tooltip.unchecked')}
                  testId={rememberMeChecked ? 'login-rememberme-tooltip-checked' : 'login-rememberme-tooltip-unchecked'}
                />
              </div>

              {/* Submit button */}
              <button
                type="submit"
                disabled={isPending || isLocked}
                aria-label={
                  isPending
                    ? t('login.submit.pending.aria')
                    : isLocked
                    ? t('login.submit.locked.aria')
                    : t('login.submit.idle')
                }
                aria-disabled={isPending || isLocked}
                className="lr-submit"
              >
                {isPending && <span role="img" aria-hidden="true" className="lr-spinner" />}
                {isPending ? t('login.submit.pending') : t('login.submit.idle')}
              </button>
            </form>
          </div>
        </div>

        {/* mono footer (P6 status pill 포함) */}
        <div className="lr-form-footer">
          <span>{t('login.form.footer')}</span>
          <span className="lr-footer-meta" aria-label={t('login.footer.aria')}>
            <span>v{APP_VERSION}</span>
            <span className="lr-status-pill" style={statusPillColor}>
              <span aria-hidden="true" className="lr-status-dot" />
              {statusPillLabel}
            </span>
          </span>
        </div>

        {/* Empty state (N/A for login page — shown only for completeness) */}
        {/* LoginPage is a form, not a list — empty state is N/A per §6.4 */}
        {false && (
          <EmptyState
            title="이 페이지는 empty 상태가 없습니다"
            description="LoginPage 는 데이터 목록 페이지가 아닌 입력 폼 페이지입니다."
          />
        )}

        {/* ── §6.7 Common Usage Panel (P7 · 폼 패널 하단 유지) ──────────── */}
        <div className="lr-common-usage-wrap">
          <CommonUsagePanel
            items={COMMON_USAGE_ITEMS}
            defaultOpen={false}
            title={t('login.commonPanel.title')}
            subtitle={t('login.commonPanel.subtitle')}
          />
        </div>
      </section>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes lr-aurora-drift {
          0%   { transform: translate(0,0) scale(1); }
          33%  { transform: translate(6%,-4%) scale(1.12); }
          66%  { transform: translate(-5%,5%) scale(0.96); }
          100% { transform: translate(0,0) scale(1); }
        }
        @keyframes lr-aurora-drift-2 {
          0%   { transform: translate(0,0) scale(1.05); }
          50%  { transform: translate(-7%,4%) scale(0.92); }
          100% { transform: translate(0,0) scale(1.05); }
        }

        .lr-page {
          display: flex;
          min-height: 100vh;
          background: var(--login-page-bg);
        }

        /* ── Brand panel ──────────────────────────────────────────────── */
        .lr-brand {
          flex: 1.08 1 0;
          position: relative;
          overflow: hidden;
          background: var(--login-brand-panel-bg);
          min-width: 0;
        }
        .lr-blob { position: absolute; border-radius: 50%; }
        .lr-blob-1 {
          width: 620px; height: 620px; left: -160px; top: -160px;
          background: radial-gradient(circle, var(--login-aurora-1) 0%, transparent 68%);
          filter: blur(36px); animation: lr-aurora-drift 18s ease-in-out infinite;
        }
        .lr-blob-2 {
          width: 560px; height: 560px; right: -160px; top: 120px;
          background: radial-gradient(circle, var(--login-aurora-2) 0%, transparent 70%);
          filter: blur(40px); animation: lr-aurora-drift-2 21s ease-in-out infinite;
        }
        .lr-blob-3 {
          width: 480px; height: 480px; left: 120px; bottom: -200px;
          background: radial-gradient(circle, var(--login-aurora-3) 0%, transparent 72%);
          filter: blur(44px); animation: lr-aurora-drift 24s ease-in-out infinite;
        }
        .lr-veil {
          position: absolute; inset: 0;
          background: linear-gradient(160deg, rgba(10,8,22,0.08), rgba(8,7,15,0.32));
        }
        .lr-brand-inner {
          position: relative; height: 100%; display: flex; flex-direction: column;
          padding: 56px 60px;
        }
        .lr-logo { display: flex; align-items: center; gap: 13px; }
        .lr-logo-mark {
          width: 42px; height: 42px; border-radius: var(--radius-lg);
          background: var(--login-gradient);
          display: flex; align-items: center; justify-content: center; color: var(--login-gradient-text);
          font-family: var(--font-family-display); font-weight: 700; font-size: 22px;
          box-shadow: 0 6px 20px rgba(98,0,238,0.5); flex-shrink: 0;
        }
        .lr-logo-text {
          font-family: var(--font-family-display); font-size: 24px; font-weight: 700;
          letter-spacing: -0.02em; color: var(--login-brand-text);
        }
        .lr-brand-body { margin-top: auto; }
        .lr-badge {
          font-family: var(--font-family-mono); font-size: 12px; letter-spacing: 0.16em;
          color: var(--login-badge-color); margin-bottom: 20px;
        }
        .lr-headline {
          margin: 0 0 18px; font-family: var(--font-family-display), var(--font-family-sans);
          font-size: 52px; line-height: 1.08; font-weight: 700; letter-spacing: -0.03em;
          color: var(--login-brand-text);
        }
        .lr-desc {
          margin: 0 0 40px; font-size: 16px; line-height: 1.7;
          color: var(--login-brand-text-muted); max-width: 440px;
        }
        .lr-features { display: flex; flex-direction: column; gap: 16px; list-style: none; padding: 0; margin: 0; }
        .lr-features li { display: flex; align-items: center; gap: 14px; font-size: 14.5px; color: var(--login-brand-text); }
        .lr-dot {
          width: 34px; height: 34px; border-radius: 9px; flex: none;
          background: color-mix(in srgb, var(--login-gradient-from) 16%, transparent);
          border: 1px solid color-mix(in srgb, var(--login-gradient-from) 30%, transparent);
          display: flex; align-items: center; justify-content: center;
          color: var(--login-badge-color); font-size: 15px;
        }
        .lr-copyright {
          margin-top: auto; padding-top: 48px; font-family: var(--font-family-mono);
          font-size: 11px; color: var(--login-brand-text-muted); letter-spacing: 0.08em;
        }

        /* ── Form panel ───────────────────────────────────────────────── */
        .lr-form-panel {
          flex: 0.92 1 0; display: flex; flex-direction: column;
          padding: 32px 40px; min-width: 0; background: var(--login-page-bg);
        }
        .lr-topbar { display: flex; justify-content: flex-end; gap: 8px; align-items: center; }
        .lr-form-wrap { margin: auto; width: 100%; max-width: 400px; }
        .lr-form-title {
          margin: 0 0 8px; font-size: 30px; font-weight: 700; letter-spacing: -0.02em;
          color: var(--color-text-primary);
        }
        .lr-form-subtitle { margin: 0 0 36px; font-size: 14.5px; color: var(--color-text-secondary); }

        .lr-field-label { display: block; font-size: 13px; font-weight: 500; color: var(--color-text-secondary); margin-bottom: 8px; }
        .lr-field-row { display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px; }
        .lr-forgot-link {
          font-size: 12.5px; color: var(--login-badge-color); cursor: pointer; background: none;
          border: none; padding: 0; font-family: var(--font-family-sans); text-decoration: none;
        }
        .lr-input {
          display: block; width: 100%; padding: 15px 16px;
          background: var(--login-input-bg); border: 1px solid var(--login-input-border);
          border-radius: var(--radius-lg); color: var(--color-text-primary); font-size: 15px;
          font-family: var(--font-family-sans); outline: none; margin-bottom: 20px;
          transition: border-color .15s, box-shadow .15s; box-sizing: border-box;
        }
        .lr-input::placeholder { color: var(--color-text-muted); }
        .lr-input:focus {
          border-color: var(--login-gradient-from);
          box-shadow: 0 0 0 3px color-mix(in srgb, var(--login-gradient-from) 18%, transparent);
        }
        .lr-input:disabled { opacity: 0.5; cursor: not-allowed; }
        .lr-input[data-invalid='true'] { border-color: var(--color-danger); }

        .lr-field-error {
          font-size: var(--font-size-xs); color: var(--color-danger);
          margin-top: -16px; margin-bottom: var(--spacing-2); display: block;
        }

        .lr-remember-row {
          display: flex; align-items: center; gap: var(--spacing-2); margin-bottom: 28px; position: relative;
        }
        .lr-remember-label {
          display: flex; align-items: center; gap: 9px; user-select: none;
          font-size: 14px; color: var(--color-text-secondary);
        }
        .lr-checkbox-input {
          appearance: none; -webkit-appearance: none; width: 19px; height: 19px;
          border-radius: 6px; border: 1px solid var(--login-input-border);
          background-color: transparent; cursor: pointer; flex-shrink: 0;
          transition: background .15s, border-color .15s;
          background-repeat: no-repeat, no-repeat;
          background-position: center, center;
          background-size: 11px, cover;
        }
        .lr-checkbox-input:checked {
          border-color: transparent;
          background-image: url("data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath d='M3 8.5l3 3 7-7' fill='none' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E"), var(--login-gradient);
        }
        .lr-checkbox-input:disabled { cursor: not-allowed; }

        .lr-submit {
          display: flex; align-items: center; justify-content: center; gap: var(--spacing-2);
          width: 100%; padding: 16px; border: none; border-radius: 13px;
          background: var(--login-gradient); color: var(--login-gradient-text); font-size: 15.5px; font-weight: 600;
          font-family: var(--font-family-sans); cursor: pointer;
          box-shadow: 0 10px 28px rgba(98,0,238,0.35);
          transition: transform .12s, box-shadow .12s; margin-bottom: var(--spacing-4);
        }
        .lr-submit:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 14px 34px rgba(98,0,238,0.45); }
        .lr-submit:active:not(:disabled) { transform: translateY(0); }
        .lr-submit:disabled { opacity: 0.6; cursor: not-allowed; }

        .lr-spinner {
          width: 16px; height: 16px; border: 2px solid var(--color-border-subtle);
          border-top-color: var(--login-gradient-text); border-radius: var(--radius-full); display: inline-block;
          animation: spin 0.8s linear infinite; flex-shrink: 0;
        }

        .lr-banner {
          border-radius: var(--radius-md); padding: var(--spacing-3) var(--spacing-4);
          margin-bottom: var(--spacing-4); display: flex; align-items: flex-start; gap: var(--spacing-3);
        }
        .lr-banner-warning {
          background: color-mix(in srgb, var(--color-warning) 10%, var(--login-input-bg));
          border: 1px solid var(--color-warning);
        }
        .lr-banner-danger {
          background: color-mix(in srgb, var(--color-danger) 12%, var(--login-input-bg));
          border: 1px solid var(--color-danger);
        }
        .lr-banner-icon { font-size: var(--font-size-lg); flex-shrink: 0; line-height: 1.4; }
        .lr-banner-title { font-size: var(--font-size-sm); font-weight: 600; margin-bottom: var(--spacing-1); }
        .lr-banner-body { font-size: var(--font-size-sm); color: var(--color-text-secondary); }
        .lr-error-chip {
          font-family: var(--font-family-mono); font-size: var(--font-size-xs); color: var(--color-text-muted);
          background: var(--login-input-bg); padding: 1px var(--spacing-1); border-radius: var(--radius-sm);
        }
        .lr-retry-btn {
          display: inline-block; margin-top: var(--spacing-2); font-size: var(--font-size-xs); font-weight: 600;
          color: var(--login-badge-color); cursor: pointer; background: none; border: none; padding: 0;
          font-family: var(--font-family-sans);
        }

        .lr-form-footer {
          margin-top: var(--spacing-6); text-align: center; font-family: var(--font-family-mono);
          font-size: 11px; color: var(--color-text-muted); letter-spacing: 0.06em;
          display: flex; flex-direction: column; gap: var(--spacing-2); align-items: center;
        }
        .lr-footer-meta {
          display: flex; gap: var(--spacing-4); align-items: center; justify-content: center; flex-wrap: wrap;
        }
        .lr-status-pill {
          display: inline-flex; align-items: center; gap: 5px; font-size: var(--font-size-xs); font-weight: 600;
          padding: 2px var(--spacing-3); border-radius: var(--radius-full); border: 1px solid currentColor;
        }
        .lr-status-dot { width: 6px; height: 6px; border-radius: var(--radius-full); background: currentColor; flex-shrink: 0; display: inline-block; }

        .lr-common-usage-wrap { width: 100%; max-width: 960px; margin: var(--spacing-6) auto 0; }

        @media (prefers-reduced-motion: reduce) {
          .lr-blob { animation: none !important; }
        }

        /* ── Responsive: stack on narrow screens (§6.9.5) ──────────────── */
        @media (max-width: 900px) {
          .lr-page { flex-direction: column; }
          .lr-brand { flex: none; min-height: 340px; }
          .lr-brand-inner { padding: 32px 28px; }
          .lr-headline { font-size: 38px; }
          .lr-form-panel { flex: none; padding: 28px 24px 40px; }
        }
      `}</style>
    </div>
  );
}
